import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import { getCities, UsCity } from '../../services/api/geographyService';

export interface CityAutocompleteProps {
  stateCode: string;
  value: string;
  onChange: (city: string) => void;
  error?: string;
  disabled?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
  position: relative;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.status.error.text};
  margin-left: ${({ theme }) => theme.spacing[1]};
`;

const InputWrapper = styled.div<{ $hasError: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 44px;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: ${({ theme }) => theme.transitions.fast};

  ${({ $hasError, theme }) =>
    $hasError &&
    css`
      border-color: ${theme.colors.status.error.text};
    `}

  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      background: ${theme.colors.backgrounds.light};
      cursor: not-allowed;
      opacity: 0.6;
    `}

  &:hover:not(:focus-within) {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.error.text : theme.colors.borders.medium};
  }

  &:focus-within {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.error.text : theme.colors.primary.base};
    box-shadow: ${({ theme, $hasError }) =>
      $hasError ? theme.shadows.ringError : theme.shadows.ring};
  }
`;

const StyledInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: none;
  background: transparent;
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  box-shadow: none;
  border-radius: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.light};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const DropdownPanel = styled.ul`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.spacing[1]});
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  margin: 0;
  padding: ${({ theme }) => theme.spacing[1]} 0;
  list-style: none;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  max-height: 220px;
  overflow-y: auto;
`;

const DropdownItem = styled.li<{ $highlighted: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  background: ${({ theme, $highlighted }) =>
    $highlighted ? theme.colors.backgrounds.light : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }
`;

const CityName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CountyName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const StatusMessage = styled.li`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.light};
  list-style: none;
`;

const HelperText = styled.span<{ $isError: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme, $isError }) =>
    $isError ? theme.colors.status.error.text : theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`;

export function CityAutocomplete({
  stateCode,
  value,
  onChange,
  error,
  disabled = false,
}: CityAutocompleteProps) {
  const [debouncedSearch, setDebouncedSearch] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep debouncedSearch in sync when value is reset externally (form reset / edit pre-fill)
  useEffect(() => {
    setDebouncedSearch(value);
  }, [value]);

  // Clear pending debounce on unmount to avoid state updates on an unmounted component
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    onChange(raw);
    setHighlightedIndex(-1);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(raw), 300);
  };

  const shouldFetch = debouncedSearch.length >= 2 && !!stateCode;

  const { data: suggestions = [], isFetching } = useQuery<UsCity[]>({
    queryKey: ['cities', stateCode, debouncedSearch],
    queryFn: () => getCities({ stateCode, search: debouncedSearch }),
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
  });

  const showDropdown = isOpen && shouldFetch && (isFetching || suggestions.length > 0);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const selectCity = (city: UsCity) => {
    onChange(city.city);
    setIsOpen(false);
    setHighlightedIndex(-1);
    // Sync debouncedSearch immediately so the query key stays consistent with value
    setDebouncedSearch(city.city);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      selectCity(suggestions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <Container ref={containerRef}>
      <Label>
        City <RequiredMark aria-hidden="true">*</RequiredMark>
      </Label>

      <InputWrapper $hasError={Boolean(error)} $disabled={disabled}>
        <StyledInput
          type="text"
          value={value}
          placeholder="Phoenix"
          disabled={disabled}
          autoComplete="off"
          aria-label="City"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </InputWrapper>

      {showDropdown && (
        <DropdownPanel role="listbox" aria-label="City suggestions">
          {isFetching && suggestions.length === 0 ? (
            <StatusMessage role="option" aria-selected={false}>
              Loading...
            </StatusMessage>
          ) : (
            suggestions.map((city, index) => (
              <DropdownItem
                key={city.id}
                role="option"
                aria-selected={index === highlightedIndex}
                $highlighted={index === highlightedIndex}
                onMouseDown={(e) => {
                  // prevent blur firing before click registers
                  e.preventDefault();
                  selectCity(city);
                }}
              >
                <CityName>{city.city}</CityName>
                {city.county && <CountyName>{city.county} County</CountyName>}
              </DropdownItem>
            ))
          )}
        </DropdownPanel>
      )}

      {error && <HelperText $isError>{error}</HelperText>}
    </Container>
  );
}

export default CityAutocomplete;
